import { useEffect, useState } from "react";
import { api } from "../lib/api";

export default function Classement() {
  const [discipline, setDiscipline] = useState("basket");
  const [standings, setStandings] = useState([]);
  const [matches, setMatches] = useState([]);
  const [bracket, setBracket] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    Promise.all([
      api.get("/standings", { params: { discipline } }),
      api.get("/matches", { params: { discipline } }),
      api.get(`/bracket/${discipline}`),
    ])
      .then(([s, m, b]) => {
        if (!mounted) return;
        setStandings(s.data);
        setMatches(m.data);
        setBracket(b.data);
      })
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, [discipline]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-20" data-testid="classement-page">
      <p className="text-xs tracking-[0.3em] uppercase text-[#FF6B1A] mb-4">Live</p>
      <h1 className="font-display text-5xl sm:text-7xl leading-none mb-8">CLASSEMENT</h1>

      <div className="flex gap-2 mb-10" data-testid="discipline-tabs">
        {[
          { v: "basket", label: "Basket 3x3" },
          { v: "foot", label: "Football 5x5" },
        ].map((d) => (
          <button
            key={d.v}
            onClick={() => setDiscipline(d.v)}
            className={`px-5 py-2 text-xs tracking-[0.2em] uppercase font-bold border-2 transition-all ${
              discipline === d.v
                ? "bg-[#FF6B1A] border-[#FF6B1A] text-black"
                : "border-white/20 text-white hover:border-[#FF6B1A]"
            }`}
            data-testid={`tab-${d.v}`}
          >
            {d.label}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-white/50">Chargement…</p>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-8">
          {/* Standings table(s) */}
          <div className="space-y-6">
            {standings.some((s) => s.pool) ? (
              [...new Set(standings.map((s) => s.pool || "—"))].sort().map((pool) => (
                <div key={pool} className="uf-card overflow-x-auto" data-testid={`standings-pool-${pool}`}>
                  <p className="font-display text-2xl px-4 pt-4 text-[#FF6B1A]">POULE {pool}</p>
                  <StandingsTable rows={standings.filter((s) => (s.pool || "—") === pool)} />
                </div>
              ))
            ) : (
              <div className="uf-card overflow-x-auto" data-testid="standings-table">
                <StandingsTable rows={standings} />
              </div>
            )}
          </div>

          {/* Matches */}
          <div className="space-y-3" data-testid="matches-list">
            <h3 className="font-display text-2xl mb-4">MATCHS</h3>
            {matches.length === 0 ? (
              <div className="uf-card p-6 text-white/50">Aucun match programmé.</div>
            ) : (
              matches.map((m) => <MatchRow key={m.id} m={m} />)
            )}
          </div>
        </div>
      )}

      {/* Bracket knockout view */}
      {!loading && Object.keys(bracket).length > 0 && (
        <div className="mt-16" data-testid="bracket-public">
          <h3 className="font-display text-3xl sm:text-4xl mb-2">PHASES FINALES</h3>
          <p className="text-white/60 mb-6 text-sm">Route vers le titre — chaque victoire mène plus loin.</p>
          <div className="overflow-x-auto">
            <div className="flex gap-6 min-w-max py-4">
              {["8e", "quart", "demi", "finale"].map((phase) => {
                const list = bracket[phase] || [];
                if (list.length === 0) return null;
                const label = { "8e": "8èmes", quart: "Quarts", demi: "Demi-finales", finale: "Finale" }[phase];
                return (
                  <div key={phase} className="min-w-[240px]">
                    <p className="text-xs tracking-[0.3em] uppercase text-[#FF6B1A] mb-4 text-center">{label}</p>
                    <div className="space-y-4 flex flex-col justify-around">
                      {list.map((m) => {
                        const wa = m.status === "finished" && m.score_a > m.score_b;
                        const wb = m.status === "finished" && m.score_b > m.score_a;
                        return (
                          <div
                            key={m.id}
                            className={`uf-card p-3 border ${phase === "finale" ? "border-[#FF6B1A] shadow-[0_0_30px_-8px_rgba(255,107,26,0.6)]" : "border-white/10"}`}
                          >
                            <div className={`flex items-center justify-between py-1 ${wa ? "text-[#FF6B1A] font-bold" : "text-white/80"}`}>
                              <span className="text-sm truncate">{m.team_a_name || "TBD"}</span>
                              <span className="font-display text-lg">{m.score_a}</span>
                            </div>
                            <div className="border-t border-white/10 my-1" />
                            <div className={`flex items-center justify-between py-1 ${wb ? "text-[#FF6B1A] font-bold" : "text-white/80"}`}>
                              <span className="text-sm truncate">{m.team_b_name || "TBD"}</span>
                              <span className="font-display text-lg">{m.score_b}</span>
                            </div>
                            <p className="text-[10px] tracking-widest uppercase text-white/40 mt-2 text-center">
                              {m.status === "finished" ? "Terminé" : m.status === "live" ? "● LIVE" : "À venir"}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StandingsTable({ rows }) {
  return (
    <table className="w-full text-sm">
      <thead className="bg-black border-b border-white/20">
        <tr className="text-xs tracking-widest uppercase text-white/50">
          <th className="text-left p-3">#</th>
          <th className="text-left p-3">Équipe</th>
          <th className="text-left p-3 hidden sm:table-cell">Quartier</th>
          <th className="p-3">J</th>
          <th className="p-3">V</th>
          <th className="p-3">N</th>
          <th className="p-3">D</th>
          <th className="p-3 hidden sm:table-cell">+/-</th>
          <th className="p-3 text-[#FF6B1A]">Pts</th>
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 ? (
          <tr>
            <td colSpan={9} className="text-center p-10 text-white/40">
              Aucune équipe classée pour l'instant.
            </td>
          </tr>
        ) : (
          rows.map((s, i) => (
            <tr key={s.team_id} className="border-b border-white/5 hover:bg-white/5" data-testid={`standing-row-${i}`}>
              <td className="p-3 font-display text-xl">{i + 1}</td>
              <td className="p-3 font-bold">{s.team_name}</td>
              <td className="p-3 text-white/60 hidden sm:table-cell">{s.quartier}</td>
              <td className="p-3 text-center">{s.played}</td>
              <td className="p-3 text-center text-[#FF6B1A]">{s.wins}</td>
              <td className="p-3 text-center">{s.draws}</td>
              <td className="p-3 text-center text-[#FF3B30]">{s.losses}</td>
              <td className="p-3 text-center hidden sm:table-cell">
                {s.points_for - s.points_against > 0 ? "+" : ""}
                {s.points_for - s.points_against}
              </td>
              <td className="p-3 text-center font-display text-2xl text-[#FF6B1A]">{s.points}</td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  );
}

function MatchRow({ m }) {
  const statusColor =
    m.status === "live" ? "text-[#FF3B30]" : m.status === "finished" ? "text-white/40" : "text-[#FF6B1A]";
  return (
    <div className="uf-card p-4" data-testid={`match-${m.id}`}>
      <div className="flex items-center justify-between mb-2">
        <span className={`text-xs tracking-widest uppercase ${statusColor}`}>
          {m.status === "live" ? "● LIVE" : m.status === "finished" ? "Terminé" : "À venir"}
        </span>
        <span className="text-xs text-white/40 uppercase">{m.phase}</span>
      </div>
      <div className="flex items-center justify-between">
        <span className="font-bold">{m.team_a_name || m.team_a_id.slice(0, 8)}</span>
        <span className="font-display text-2xl text-[#FF6B1A]">
          {m.score_a} - {m.score_b}
        </span>
        <span className="font-bold text-right">{m.team_b_name || m.team_b_id.slice(0, 8)}</span>
      </div>
    </div>
  );
}
